import React, { useId, useState } from 'react';
import { Button, Dropdown, Form, Modal } from 'react-bootstrap';
import OfferDialog from '@/components/hiring/OfferDialog';
import {
    ChooseCandidateDialog,
    ReleaseToPoolDialog,
} from '@/components/hiring/OfferChoiceDialogs';
import ReleaseReasonDialog from '@/components/hiring/ReleaseReasonDialog';
import { candidacyService } from '@/shared/services/candidacyService';
import { PREVIEW_LOCK_NOTE_ID } from '@/shared/hooks/usePreviewLock';
import InterviewDetailsForm from './InterviewDetailsForm';
import {
    CONFIRM_MOVES,
    OFFER_FROM,
    OPTIONAL_INTERVIEW_TEXT,
    OPTIONAL_STAGES,
    SAVE_FALLBACK,
    WriteStatus,
    moveLabelOf,
    refusalOf,
    reviewerStageOf,
} from './reviewerStages';

const NOTE_MAX = 1000;
// A move to interview from these is offered as "Schedule interview": saving
// the details moves the candidate there and emails the student.
const BEFORE_INTERVIEW = ['applied', 'screening'];

const CONFIRM_TEXT = {
    not_selected: {
        title: 'Mark not selected',
        body: (name) => `Mark ${name} not selected? The student is emailed.`,
        confirm: 'Mark not selected',
    },
    closed: {
        title: 'Mark no longer available',
        body: (name) =>
            `Mark ${name}'s application no longer available? The student is emailed.`,
        confirm: 'Mark no longer available',
    },
};

/**
 * What a reviewer may do to one candidacy, as one action bar: the step that
 * moves the candidate on as a button (Make an offer is the primary one at
 * Interview and Finalist; at Applied and Being reviewed it sits beside
 * Schedule interview as an equal choice), and every other move, the offer
 * follow-ups and the ending actions in a More menu, ending actions last and
 * always confirmed. Stage moves come only from the candidacy's allowedMoves,
 * which the server computes for this caller; `offered` in that list is the
 * offer, never a stage move. Take back, Rescind, Release and the expired
 * offer's choices follow `access`, which allowedMoves cannot express. A
 * coordinator's move asks for an optional note for the other reviewers;
 * faculty never see stage notes, so theirs is made at once.
 */
export default function CandidateStageControls({
    candidacy,
    position,
    access,
    mode,
    disabled,
    siblings = [],
    onChange,
    onReload,
    onReloadAndFocus,
}) {
    const id = useId();
    const coordinator = mode === 'coordinator';
    const name = candidacy.student?.name || 'this student';
    const { stage } = candidacy;
    const [busy, setBusy] = useState(false);
    const [refusal, setRefusal] = useState(null);
    const [notice, setNotice] = useState('');
    const [note, setNote] = useState('');
    const [confirming, setConfirming] = useState(null);
    const [offerOpen, setOfferOpen] = useState(false);
    const [scheduling, setScheduling] = useState(false);
    const [takeBackNote, setTakeBackNote] = useState('');
    const [releasing, setReleasing] = useState(null);
    // 'again' (the same student), 'other' (choosing), 'pool' (confirming)
    const [expiredChoice, setExpiredChoice] = useState(null);
    const [otherCandidate, setOtherCandidate] = useState(null);
    const [poolError, setPoolError] = useState(null);

    const moves = (candidacy.allowedMoves || []).filter((m) => m !== 'offered');
    // An expired offer holds its place until a choice is made; Offer again
    // replaces Make an offer for it.
    const expired = stage === 'expired' && Boolean(access?.canOffer);
    const canOffer =
        (candidacy.allowedMoves || []).includes('offered') && !expired;
    const otherChoices = siblings.filter(
        (other) =>
            other.id !== candidacy.id &&
            OFFER_FROM.includes(other.stage) &&
            other.stage !== 'expired'
    );
    const canTakeBack = stage === 'offered' && access?.canOffer;
    const canRescind = stage === 'offered' && access?.canOffer;
    const canRelease = stage === 'accepted' && access?.canOffer;
    const canSchedule =
        moves.includes('interview') && BEFORE_INTERVIEW.includes(stage);
    const finalistButton = stage === 'interview' && moves.includes('finalist');
    const noteTooLong = note.length > NOTE_MAX;
    const lockedDescription = disabled ? PREVIEW_LOCK_NOTE_ID : undefined;
    const writeDisabled = disabled || busy;

    const run = async (action, message) => {
        setBusy(true);
        setRefusal(null);
        setNotice('');
        try {
            const updated = await action();
            const text = typeof message === 'function' ? message() : message;
            setNotice(text);
            onChange?.(updated, text);
            return true;
        } catch (err) {
            setRefusal(refusalOf(err, SAVE_FALLBACK));
            return false;
        } finally {
            setBusy(false);
        }
    };

    const sentNote = (text) =>
        coordinator && text.trim() !== '' ? text : undefined;

    const move = async (to) => {
        const moved = await run(
            () => candidacyService.moveStage(candidacy.id, to, sentNote(note)),
            `Moved to ${reviewerStageOf(to).label}.`
        );
        if (moved) setNote('');
    };

    const choose = (to) => {
        if (coordinator || CONFIRM_MOVES.includes(to)) {
            setConfirming(to);
            return;
        }
        move(to);
    };

    const confirmMove = async () => {
        const to = confirming;
        setConfirming(null);
        await move(to);
    };

    const takeBack = async () => {
        setConfirming(null);
        const tookBack = await run(
            () =>
                candidacyService.takeBackOffer(
                    candidacy.id,
                    sentNote(takeBackNote)
                ),
            `Took back the offer to ${name}.`
        );
        if (tookBack) setTakeBackNote('');
    };

    // The dialog keeps the stage and offer it opened on, so a confirm sent
    // after the candidacy changed is refused rather than ending a place the
    // reviewer never saw.
    const openRelease = (kind) =>
        setReleasing({
            kind,
            stage,
            offeredAt: candidacy.offer?.offeredAt ?? null,
        });

    const release = async (reason) => {
        const { kind, stage: shownStage, offeredAt } = releasing;
        const outcome = kind === 'rescind' ? 'Offer rescinded.' : 'Released.';
        setReleasing(null);
        let next = null;
        await run(
            async () => {
                const answer = await candidacyService.release(
                    candidacy.id,
                    reason,
                    { stage: shownStage, offeredAt }
                );
                next = answer.nextCandidate;
                return answer.candidacy;
            },
            () =>
                next?.name
                    ? `${outcome} Next in your ranking: ${next.name}.`
                    : `${outcome} No candidate is left in your ranking.`
        );
    };

    const releaseToPool = async () => {
        setPoolError(null);
        const done = await run(
            async () =>
                (await candidacyService.releaseExpired(candidacy.id)).candidacy,
            'The place is open to students who applied.'
        );
        if (done) setExpiredChoice(null);
        else setPoolError(true);
    };

    // The buttons in view, in order, and the More menu's two groups: other
    // steps, then the actions that end something.
    const buttons = [];
    if (canOffer) {
        buttons.push({
            key: 'offer',
            label: 'Make an offer',
            variant: OPTIONAL_STAGES.includes(stage)
                ? 'primary'
                : 'outline-primary',
            onClick: () => setOfferOpen(true),
        });
    }
    if (expired) {
        buttons.push({
            key: 'again',
            label: 'Offer again',
            variant: 'primary',
            onClick: () => setExpiredChoice('again'),
        });
    }
    if (canSchedule) {
        buttons.push({
            key: 'schedule',
            label: 'Schedule interview',
            variant: 'outline-primary',
            onClick: () => setScheduling(true),
        });
    }
    if (finalistButton) {
        buttons.push({
            key: 'finalist',
            label: moveLabelOf('finalist'),
            variant: 'outline-primary',
            onClick: () => choose('finalist'),
        });
    }

    const steps = moves
        .filter((to) => !CONFIRM_MOVES.includes(to))
        .filter((to) => !(finalistButton && to === 'finalist'))
        .map((to) => ({
            key: to,
            label: moveLabelOf(to),
            onClick: () => choose(to),
        }));
    if (expired) {
        if (otherChoices.length > 0) {
            steps.push({
                key: 'other',
                label: 'Offer to another candidate',
                onClick: () => setExpiredChoice('other'),
            });
        }
        steps.push({
            key: 'pool',
            label: 'Release to applicants',
            onClick: () => setExpiredChoice('pool'),
        });
    }
    if (canTakeBack) {
        steps.push({
            key: 'takeBack',
            label: 'Take back the offer',
            onClick: () => setConfirming('takeBack'),
        });
    }
    const endings = moves
        .filter((to) => CONFIRM_MOVES.includes(to))
        .map((to) => ({
            key: to,
            label: moveLabelOf(to),
            onClick: () => choose(to),
        }));
    if (canRescind) {
        endings.push({
            key: 'rescind',
            label: 'End the application',
            onClick: () => openRelease('rescind'),
        });
    }
    if (canRelease) {
        endings.push({
            key: 'release',
            label: 'Release from this position',
            onClick: () => openRelease('release'),
        });
    }
    const hasMenu = steps.length + endings.length > 0;
    const hasBar = buttons.length > 0 || hasMenu;

    const hiddenName = <span className="visually-hidden">{` ${name}`}</span>;
    const confirmTitleId = `${id}-confirm-title`;
    const takeBackTitleId = `${id}-takeback-title`;
    const scheduleTitleId = `${id}-schedule-title`;
    const ending = CONFIRM_TEXT[confirming];
    const progressing =
        confirming && confirming !== 'takeBack' && !ending ? confirming : null;

    return (
        <div>
            {hasBar && (
                <div
                    className="hiring-action-bar d-flex flex-wrap align-items-center gap-2"
                    role="group"
                    aria-label={`Actions for ${name}`}
                >
                    {buttons.map((button) => (
                        <Button
                            key={button.key}
                            variant={button.variant}
                            className="hiring-action"
                            disabled={writeDisabled}
                            aria-describedby={lockedDescription}
                            onClick={button.onClick}
                        >
                            {button.label}
                            {hiddenName}
                        </Button>
                    ))}
                    {hasMenu && (
                        <Dropdown className="hiring-action hiring-more">
                            <Dropdown.Toggle
                                variant="outline-primary"
                                id={`${id}-more`}
                                className="w-100"
                                disabled={writeDisabled}
                                aria-describedby={lockedDescription}
                            >
                                More
                                <span className="visually-hidden">
                                    {` actions for ${name}`}
                                </span>
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                {steps.map((item) => (
                                    <Dropdown.Item
                                        key={item.key}
                                        as="button"
                                        onClick={item.onClick}
                                    >
                                        {item.label}
                                    </Dropdown.Item>
                                ))}
                                {steps.length > 0 && endings.length > 0 && (
                                    <Dropdown.Divider />
                                )}
                                {endings.map((item) => (
                                    <Dropdown.Item
                                        key={item.key}
                                        as="button"
                                        className="text-danger"
                                        onClick={item.onClick}
                                    >
                                        {item.label}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    )}
                </div>
            )}
            {canOffer && BEFORE_INTERVIEW.includes(stage) && (
                <p className="small text-body-secondary mt-2 mb-0">
                    {OPTIONAL_INTERVIEW_TEXT}
                </p>
            )}
            <WriteStatus
                refusal={refusal}
                notice={notice}
                onReload={onReload}
            />
            <Modal
                show={Boolean(ending || progressing)}
                onHide={() => setConfirming(null)}
                aria-labelledby={confirmTitleId}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title as="h2" className="h5" id={confirmTitleId}>
                        {ending
                            ? ending.title
                            : progressing &&
                              `Move ${name} to ${reviewerStageOf(progressing).label}`}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {ending && <p>{ending.body(name)}</p>}
                    {coordinator && (
                        <Form.Group controlId={`${id}-note`}>
                            <Form.Label>
                                Note for reviewers (optional)
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                maxLength={NOTE_MAX}
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                aria-describedby={`${id}-note-count`}
                            />
                            <Form.Text id={`${id}-note-count`} as="div" muted>
                                {noteTooLong
                                    ? `A note can be at most ${NOTE_MAX.toLocaleString('en-US')} characters.`
                                    : `${note.length.toLocaleString('en-US')} of ${NOTE_MAX.toLocaleString('en-US')} characters. Faculty never see this note.`}
                            </Form.Text>
                        </Form.Group>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-primary"
                        onClick={() => setConfirming(null)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant={ending ? 'danger' : 'primary'}
                        disabled={noteTooLong}
                        onClick={confirmMove}
                    >
                        {ending
                            ? ending.confirm
                            : progressing && moveLabelOf(progressing)}
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={confirming === 'takeBack'}
                onHide={() => setConfirming(null)}
                aria-labelledby={takeBackTitleId}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title as="h2" className="h5" id={takeBackTitleId}>
                        Take back the offer
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        {`Take back the offer to ${name}? The place opens again and the student is emailed.`}
                    </p>
                    {coordinator && (
                        <Form.Group controlId={`${id}-takeback-note`}>
                            <Form.Label>Note for reviewers</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                maxLength={NOTE_MAX}
                                value={takeBackNote}
                                onChange={(e) =>
                                    setTakeBackNote(e.target.value)
                                }
                            />
                        </Form.Group>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-primary"
                        onClick={() => setConfirming(null)}
                    >
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={takeBack}>
                        Take back the offer
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={scheduling}
                onHide={() => setScheduling(false)}
                aria-labelledby={scheduleTitleId}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title as="h2" className="h5" id={scheduleTitleId}>
                        {`Schedule an interview with ${name}`}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="small text-body-secondary">
                        Saving moves {name} to Interview and emails them the
                        details.
                    </p>
                    {scheduling && (
                        <InterviewDetailsForm
                            candidacy={candidacy}
                            disabled={disabled}
                            onChange={(updated) => {
                                const message = `Interview scheduled with ${name}. The student has been emailed.`;
                                setScheduling(false);
                                setNotice(message);
                                onChange?.(updated, message);
                            }}
                            onReload={() => {
                                setScheduling(false);
                                onReload?.();
                            }}
                        />
                    )}
                </Modal.Body>
            </Modal>
            <ReleaseReasonDialog
                show={Boolean(releasing)}
                side="reviewer"
                title={
                    releasing?.kind === 'rescind'
                        ? `End ${name}'s application?`
                        : `Release ${name} from this position?`
                }
                intro={
                    releasing?.kind === 'rescind'
                        ? 'Their application to this position ends, the place opens again, and the student is told the offer was withdrawn. To keep them in the running, take the offer back instead.'
                        : "Their assignment ends, the place opens again, and the student, the section's faculty and the program are emailed."
                }
                confirmLabel={
                    releasing?.kind === 'rescind'
                        ? 'End the application'
                        : 'Release from this position'
                }
                onConfirm={release}
                onHide={() => setReleasing(null)}
            />
            <ReleaseToPoolDialog
                show={expiredChoice === 'pool'}
                name={name}
                busy={busy}
                error={
                    poolError && refusal
                        ? {
                              message: refusal.message,
                              conflict: refusal.conflict,
                          }
                        : null
                }
                onConfirm={releaseToPool}
                onHide={() => setExpiredChoice(null)}
                onReload={() => {
                    setExpiredChoice(null);
                    onReload?.();
                }}
            />
            <ChooseCandidateDialog
                show={expiredChoice === 'other'}
                name={name}
                choices={otherChoices.map((other) => ({
                    id: other.id,
                    label: other.student?.name || 'A student',
                }))}
                onNext={(chosen) => {
                    setOtherCandidate(
                        otherChoices.find((other) => other.id === chosen)
                    );
                    setExpiredChoice(null);
                }}
                onHide={() => setExpiredChoice(null)}
            />
            {otherCandidate && (
                <OfferDialog
                    show
                    candidacy={otherCandidate}
                    position={position}
                    mode={mode}
                    releaseFrom={candidacy.id}
                    onHide={() => setOtherCandidate(null)}
                    onOffered={() => {
                        const chosen = otherCandidate;
                        const message = `Offer made to ${chosen.student?.name || 'the candidate'}.`;
                        setOtherCandidate(null);
                        setNotice(message);
                        if (onReloadAndFocus) {
                            onReloadAndFocus(chosen.id, message);
                        } else onReload?.();
                    }}
                />
            )}
            {(offerOpen || expiredChoice === 'again') && (
                <OfferDialog
                    show
                    candidacy={candidacy}
                    position={position}
                    mode={mode}
                    onHide={() => {
                        setOfferOpen(false);
                        setExpiredChoice(null);
                    }}
                    onOffered={(updated) => {
                        setOfferOpen(false);
                        setExpiredChoice(null);
                        const message = `Offer made to ${name}.`;
                        setNotice(message);
                        onChange?.(updated, message);
                    }}
                />
            )}
        </div>
    );
}
